import type { TshirtSide } from "./tshirts";

export type TshirtGuide = {
  readonly guideVersion: 1;
  readonly guideScale: number;
  readonly guideCenterX: number;
  readonly guideCenterY: number;
};

export type SegmentationMask = {
  readonly width: number;
  readonly height: number;
  readonly alpha: Uint8ClampedArray;
  readonly source: SegmentationSource;
  readonly opaqueRatio: number;
  readonly diagnostics: SegmentationDiagnostics;
};

export type SegmentationSource = "mediapipe" | "guide-fallback";

export type SegmentationBoundingBox = {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
};

export type SegmentationDiagnostics = {
  readonly source: SegmentationSource;
  readonly maskWidth: number;
  readonly maskHeight: number;
  readonly outputWidth: number;
  readonly outputHeight: number;
  readonly opaqueRatio: number;
  readonly alphaMin: number;
  readonly alphaMax: number;
  readonly alphaMean: number;
  readonly boundingBox: SegmentationBoundingBox | null;
  readonly promptPoint: NormalizedPoint;
  readonly guideOpaqueRatio: number;
  readonly guideIntersectionOverUnion: number;
  readonly differsFromGuideRatio: number;
  readonly primaryErrorMessage?: string;
};

export type MaskSize = {
  readonly width: number;
  readonly height: number;
};

type NormalizedPoint = {
  readonly x: number;
  readonly y: number;
};

export type ProcessedTshirtImage = {
  readonly side: TshirtSide;
  readonly blob: Blob;
  readonly width: number;
  readonly height: number;
  readonly objectUrl: string;
  readonly segmentationSource: SegmentationSource;
  readonly segmentationOpaqueRatio: number;
  readonly segmentationDiagnostics: SegmentationDiagnostics;
  readonly maskPreviewObjectUrl?: string;
};

export type ProcessImageRequest = {
  readonly id: string;
  readonly type: "process-image";
  readonly file: File;
  readonly side: TshirtSide;
  readonly guide: TshirtGuide;
  readonly debugSegmentation: boolean;
};

export type WarmupImageWorkerRequest = {
  readonly id: string;
  readonly type: "warmup-image-worker";
};

export type ProcessImageSuccess = {
  readonly id: string;
  readonly type: "process-image-success";
  readonly image: ProcessedTshirtImage;
};

export type ProcessImageFailure = {
  readonly id: string;
  readonly type: "process-image-failure";
  readonly message: string;
};

export type ProcessImageStatus = {
  readonly id: string;
  readonly type: "process-image-status";
  readonly message: string;
};

export type ImageWorkerReady = {
  readonly id: string;
  readonly type: "image-worker-ready";
};

export type ImageWorkerRequest = ProcessImageRequest | WarmupImageWorkerRequest;

export type ImageWorkerResponse =
  | ProcessImageSuccess
  | ProcessImageFailure
  | ProcessImageStatus
  | ImageWorkerReady;

export const defaultTshirtGuide: TshirtGuide = {
  guideVersion: 1,
  guideScale: 0.82,
  guideCenterX: 0.5,
  guideCenterY: 0.48,
};

export const createOpaqueMask = (params: {
  readonly width: number;
  readonly height: number;
}): SegmentationMask => ({
  width: params.width,
  height: params.height,
  alpha: new Uint8ClampedArray(params.width * params.height).fill(255),
  source: "guide-fallback",
  opaqueRatio: 1,
  diagnostics: createSegmentationDiagnostics({
    source: "guide-fallback",
    alpha: new Uint8ClampedArray(params.width * params.height).fill(255),
    maskSize: {
      width: params.width,
      height: params.height,
    },
    outputSize: {
      width: params.width,
      height: params.height,
    },
    guide: defaultTshirtGuide,
  }),
});

const tshirtGuideBasePolygon = [
  { x: 0.3, y: 0.1 },
  { x: 0.41, y: 0.16 },
  { x: 0.46, y: 0.25 },
  { x: 0.54, y: 0.25 },
  { x: 0.59, y: 0.16 },
  { x: 0.7, y: 0.1 },
  { x: 0.93, y: 0.28 },
  { x: 0.82, y: 0.46 },
  { x: 0.73, y: 0.4 },
  { x: 0.73, y: 0.92 },
  { x: 0.27, y: 0.92 },
  { x: 0.27, y: 0.4 },
  { x: 0.18, y: 0.46 },
  { x: 0.07, y: 0.28 },
] as const satisfies readonly NormalizedPoint[];

export const createTshirtGuidePolygon = (
  guide: TshirtGuide,
): readonly NormalizedPoint[] =>
  tshirtGuideBasePolygon.map((point) => ({
    x: guide.guideCenterX + (point.x - 0.5) * guide.guideScale,
    y: guide.guideCenterY + (point.y - 0.5) * guide.guideScale,
  }));

const isPointInPolygon = (
  point: NormalizedPoint,
  polygon: readonly NormalizedPoint[],
): boolean =>
  polygon.reduce((inside, currentPoint, index) => {
    const previousPoint =
      polygon[(index + polygon.length - 1) % polygon.length];
    const crossesY = currentPoint.y > point.y !== previousPoint.y > point.y;
    const crossX =
      ((previousPoint.x - currentPoint.x) * (point.y - currentPoint.y)) /
        (previousPoint.y - currentPoint.y) +
      currentPoint.x;
    const crosses = crossesY && point.x < crossX;

    return crosses ? !inside : inside;
  }, false);

const createTshirtGuideMaskAlpha = (params: {
  readonly width: number;
  readonly height: number;
  readonly guide: TshirtGuide;
}): Uint8ClampedArray => {
  const polygon = createTshirtGuidePolygon(params.guide);

  return Uint8ClampedArray.from(
    { length: params.width * params.height },
    (_, index) =>
      isPointInPolygon(
        {
          x: (index % params.width) / params.width,
          y: Math.floor(index / params.width) / params.height,
        },
        polygon,
      )
        ? 255
        : 0,
  );
};

export const createTshirtGuideMask = (params: {
  readonly width: number;
  readonly height: number;
  readonly guide: TshirtGuide;
  readonly primaryErrorMessage?: string;
}): SegmentationMask => {
  const alpha = createTshirtGuideMaskAlpha(params);

  return {
    width: params.width,
    height: params.height,
    alpha,
    source: "guide-fallback",
    opaqueRatio: calculateOpaqueRatio(alpha),
    diagnostics: createSegmentationDiagnostics({
      source: "guide-fallback",
      alpha,
      maskSize: {
        width: params.width,
        height: params.height,
      },
      outputSize: {
        width: params.width,
        height: params.height,
      },
      guide: params.guide,
      primaryErrorMessage: params.primaryErrorMessage,
    }),
  };
};

export const calculateOpaqueRatio = (alpha: Uint8ClampedArray): number =>
  alpha.length === 0
    ? 0
    : alpha.filter((value) => value > 0).length / alpha.length;

const calculateAlphaMean = (alpha: Uint8ClampedArray): number =>
  alpha.length === 0
    ? 0
    : alpha.reduce((total, value) => total + value, 0) / alpha.length;

const calculateAlphaMin = (alpha: Uint8ClampedArray): number =>
  alpha.length === 0
    ? 0
    : alpha.reduce(
        (minimum, value) => Math.min(minimum, value),
        Number.POSITIVE_INFINITY,
      );

const calculateAlphaMax = (alpha: Uint8ClampedArray): number =>
  alpha.length === 0
    ? 0
    : alpha.reduce((maximum, value) => Math.max(maximum, value), 0);

const createBoundingBoxAccumulator = (width: number) => ({
  minX: width,
  minY: Number.POSITIVE_INFINITY,
  maxX: -1,
  maxY: -1,
});

export const calculateAlphaBoundingBox = (params: {
  readonly alpha: Uint8ClampedArray;
  readonly width: number;
}): SegmentationBoundingBox | null => {
  const box = params.alpha.reduce((accumulator, value, index) => {
    const x = index % params.width;
    const y = Math.floor(index / params.width);

    return value > 0
      ? {
          minX: Math.min(accumulator.minX, x),
          minY: Math.min(accumulator.minY, y),
          maxX: Math.max(accumulator.maxX, x),
          maxY: Math.max(accumulator.maxY, y),
        }
      : accumulator;
  }, createBoundingBoxAccumulator(params.width));

  return box.maxX < 0
    ? null
    : {
        x: box.minX,
        y: box.minY,
        width: box.maxX - box.minX + 1,
        height: box.maxY - box.minY + 1,
      };
};

const calculateMaskIntersectionCount = (params: {
  readonly alpha: Uint8ClampedArray;
  readonly guideAlpha: Uint8ClampedArray;
}): number =>
  params.alpha.filter(
    (value, index) => value > 0 && (params.guideAlpha[index] ?? 0) > 0,
  ).length;

const calculateMaskUnionCount = (params: {
  readonly alpha: Uint8ClampedArray;
  readonly guideAlpha: Uint8ClampedArray;
}): number =>
  params.alpha.filter(
    (value, index) => value > 0 || (params.guideAlpha[index] ?? 0) > 0,
  ).length;

const calculateDifferingMaskCount = (params: {
  readonly alpha: Uint8ClampedArray;
  readonly guideAlpha: Uint8ClampedArray;
}): number =>
  params.alpha.filter(
    (value, index) => value > 0 !== (params.guideAlpha[index] ?? 0) > 0,
  ).length;

export const createSegmentationDiagnostics = (params: {
  readonly source: SegmentationSource;
  readonly alpha: Uint8ClampedArray;
  readonly maskSize: MaskSize;
  readonly outputSize: MaskSize;
  readonly guide: TshirtGuide;
  readonly primaryErrorMessage?: string;
}): SegmentationDiagnostics => {
  const guideAlpha = createTshirtGuideMaskAlpha({
    width: params.outputSize.width,
    height: params.outputSize.height,
    guide: params.guide,
  });
  const intersectionCount = calculateMaskIntersectionCount({
    alpha: params.alpha,
    guideAlpha,
  });
  const unionCount = calculateMaskUnionCount({
    alpha: params.alpha,
    guideAlpha,
  });

  return {
    source: params.source,
    maskWidth: params.maskSize.width,
    maskHeight: params.maskSize.height,
    outputWidth: params.outputSize.width,
    outputHeight: params.outputSize.height,
    opaqueRatio: calculateOpaqueRatio(params.alpha),
    alphaMin: calculateAlphaMin(params.alpha),
    alphaMax: calculateAlphaMax(params.alpha),
    alphaMean: calculateAlphaMean(params.alpha),
    boundingBox: calculateAlphaBoundingBox({
      alpha: params.alpha,
      width: params.outputSize.width,
    }),
    promptPoint: {
      x: Math.min(1, Math.max(0, params.guide.guideCenterX)),
      y: Math.min(1, Math.max(0, params.guide.guideCenterY)),
    },
    guideOpaqueRatio: calculateOpaqueRatio(guideAlpha),
    guideIntersectionOverUnion:
      unionCount === 0 ? 0 : intersectionCount / unionCount,
    differsFromGuideRatio:
      params.alpha.length === 0
        ? 0
        : calculateDifferingMaskCount({
            alpha: params.alpha,
            guideAlpha,
          }) / params.alpha.length,
    ...(params.primaryErrorMessage
      ? { primaryErrorMessage: params.primaryErrorMessage }
      : {}),
  };
};

const createNeighborIndexes = (params: {
  readonly index: number;
  readonly width: number;
  readonly height: number;
}): readonly number[] => {
  const x = params.index % params.width;
  const y = Math.floor(params.index / params.width);

  return [
    ...(x > 0 ? [params.index - 1] : []),
    ...(x < params.width - 1 ? [params.index + 1] : []),
    ...(y > 0 ? [params.index - params.width] : []),
    ...(y < params.height - 1 ? [params.index + params.width] : []),
  ];
};

const collectConnectedOpaqueComponent = (params: {
  readonly alpha: Uint8ClampedArray;
  readonly visited: Uint8Array;
  readonly startIndex: number;
  readonly width: number;
  readonly height: number;
}): readonly number[] => {
  const stack = [params.startIndex];
  const component: number[] = [];

  params.visited[params.startIndex] = 1;

  while (stack.length > 0) {
    const index = stack.pop() ?? 0;

    component.push(index);
    createNeighborIndexes({
      index,
      width: params.width,
      height: params.height,
    }).map((neighborIndex) =>
      params.visited[neighborIndex] || (params.alpha[neighborIndex] ?? 0) <= 0
        ? null
        : ((params.visited[neighborIndex] = 1), stack.push(neighborIndex)),
    );
  }

  return component;
};

export const removeAlphaIslands = (params: {
  readonly alpha: Uint8ClampedArray;
  readonly width: number;
  readonly height: number;
}): Uint8ClampedArray => {
  const visited = new Uint8Array(params.alpha.length);
  const largestComponent: { indexes: readonly number[] } = { indexes: [] };
  const cursor = { index: 0 };

  while (cursor.index < params.alpha.length) {
    const index = cursor.index;

    cursor.index += 1;
    visited[index] || (params.alpha[index] ?? 0) <= 0
      ? null
      : (() => {
          const component = collectConnectedOpaqueComponent({
            alpha: params.alpha,
            visited,
            startIndex: index,
            width: params.width,
            height: params.height,
          });

          component.length > largestComponent.indexes.length
            ? (largestComponent.indexes = component)
            : null;
        })();
  }

  const selectedIndexes = largestComponent.indexes.reduce((selected, index) => {
    selected[index] = 1;

    return selected;
  }, new Uint8Array(params.alpha.length));

  return largestComponent.indexes.length === 0
    ? new Uint8ClampedArray(params.alpha.length)
    : Uint8ClampedArray.from({ length: params.alpha.length }, (_, index) =>
        selectedIndexes[index] ? (params.alpha[index] ?? 0) : 0,
      );
};

export const refineSegmentationMask = (params: {
  readonly mask: SegmentationMask;
  readonly guide: TshirtGuide;
}): SegmentationMask => {
  const alpha = removeAlphaIslands({
    alpha: params.mask.alpha,
    width: params.mask.width,
    height: params.mask.height,
  });

  return {
    ...params.mask,
    alpha,
    opaqueRatio: calculateOpaqueRatio(alpha),
    diagnostics: createSegmentationDiagnostics({
      source: params.mask.source,
      alpha,
      maskSize: {
        width: params.mask.width,
        height: params.mask.height,
      },
      outputSize: {
        width: params.mask.width,
        height: params.mask.height,
      },
      guide: params.guide,
      primaryErrorMessage: params.mask.diagnostics.primaryErrorMessage,
    }),
  };
};

export const calculateMaximizedBoundingBoxSize = (params: {
  readonly boundingBox: SegmentationBoundingBox;
  readonly maxLongSide: number;
}): MaskSize => {
  const ratio =
    params.maxLongSide /
    Math.max(params.boundingBox.width, params.boundingBox.height);

  return {
    width: Math.max(1, Math.round(params.boundingBox.width * ratio)),
    height: Math.max(1, Math.round(params.boundingBox.height * ratio)),
  };
};

export const calculateContainedSize = (params: {
  readonly width: number;
  readonly height: number;
  readonly maxLongSide: number;
}): { readonly width: number; readonly height: number } => {
  const ratio = Math.min(
    1,
    params.maxLongSide / Math.max(params.width, params.height),
  );

  return {
    width: Math.round(params.width * ratio),
    height: Math.round(params.height * ratio),
  };
};
